import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { msalInstance, getActiveAccount, loginRedirect, logoutRedirect, acquireApiToken, } from '@/auth/msalConfig';
import { useMe } from '@/composables/useMe';
function legacyClaimsToMeDto(claims) {
    const firstRole = claims.roles[0] ?? null;
    return {
        userId: claims.sub,
        email: claims.email,
        displayName: claims.name,
        role: firstRole,
        hoaId: claims.hoaId,
        hoaName: null,
        hoaSlug: null,
        residentStatus: null,
        memberId: null,
        residentId: null,
    };
}
export const useAuth = defineStore('auth', () => {
    const me = ref(null);
    const ready = ref(false);
    // Cached token — used by getAccessToken() / api/client.ts authMiddleware.
    const _token = ref(null);
    const isAuthenticated = computed(() => getActiveAccount() != null || me.value != null);
    const isBoard = computed(() => me.value?.role?.startsWith('Board.') ?? false);
    const isResident = computed(() => me.value?.role === 'Resident' || me.value?.role === 'Resident.Pending');
    const isPending = computed(() => me.value?.role === 'Resident.Pending');
    const isOnboarding = computed(() => isAuthenticated.value && me.value?.role == null);
    // Convenience aliases kept for backward-compat with existing components.
    // user is a direct alias for me (same Ref); components can use either name.
    const user = me;
    const hoaId = computed(() => me.value?.hoaId ?? null);
    /** token ref exposed for api/client.ts (read via getAccessToken). */
    const token = _token;
    async function hydrate() {
        await msalInstance.initialize();
        await msalInstance.handleRedirectPromise();
        if (getActiveAccount()) {
            const { fetchMe } = useMe();
            me.value = await fetchMe();
        }
        ready.value = true;
    }
    async function getAccessToken() {
        if (_token.value)
            return _token.value;
        const t = await acquireApiToken();
        if (t)
            _token.value = t;
        return t;
    }
    /** Redirect to CIAM for sign-in. Works for both board and resident audiences
     *  (unified single CIAM tenant). Optional domain_hint bypasses CIAM IdP picker. */
    async function login(idp) {
        await loginRedirect();
        void idp; // domain_hint support can be added when CIAM custom-policy lands
    }
    /** Board-specific login alias (called from BoardLoginView). */
    async function loginBoard() {
        await loginRedirect();
    }
    /** Resident login — same CIAM instance; idp hint preserved for future use. */
    async function loginResident(idp) {
        void idp;
        await loginRedirect();
    }
    async function logout() {
        me.value = null;
        _token.value = null;
        await logoutRedirect();
    }
    /**
     * Test-only setter for Playwright smoke tests and Vitest unit tests.
     * Accepts the legacy AuthClaims shape so existing tests compile without change.
     */
    function __setTestSession(testToken, testClaims) {
        _token.value = testToken;
        me.value = legacyClaimsToMeDto(testClaims);
    }
    return {
        // state
        me, ready, token,
        // computed
        isAuthenticated, isBoard, isResident, isPending, isOnboarding,
        user, hoaId,
        // actions
        hydrate,
        login,
        loginBoard,
        loginResident,
        logout,
        getAccessToken,
        __setTestSession,
    };
});
